import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { startEditImagesBatch } from "../../../../actions/user";
import { getIsBlockedActions } from "../../../../actions/getters";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import { validateModalImageEditTextFields } from "../../../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import { mergeAttachmentFileNames } from "./imageEditCheckpoint";
import {
  BATCH_ITEM_STATUS,
  buildBatchItemsForJob,
  buildBatchPromptsForJob,
  enrichLastTurnWithResultUrls,
  getBatchCheckpointState,
  getBatchResultImages,
  getCompletedBatchTurns,
  normalizeBatchPrompts,
} from "../../../../utils/batchImageEditJob";

import { LaunchError } from "../../../../utils/errors";

export { BATCH_ITEM_STATUS };

const buildInitialItemsMap = (initialItems = []) => {
  const map = {};

  initialItems.forEach((item) => {
    if (!item?.selectionId || !item?.url) return;

    map[item.selectionId] = {
      selectionId: item.selectionId,
      sourceItem: item.sourceItem,
      index: item.index,
      blockBaseUrl: item.url,
      versions: {
        1: { url: item.url, status: BATCH_ITEM_STATUS.SUCCESS },
      },
    };
  });

  return map;
};

const buildRestoredBatchEditState = ({
  initialItems = [],
  initialBatchImageEditSession = null,
} = {}) => {
  if (initialBatchImageEditSession?.fromAlert) {
    return {
      itemsMap:
        initialBatchImageEditSession.itemsMap ||
        buildInitialItemsMap(initialBatchImageEditSession.initialItems || []),
      globalVersion: initialBatchImageEditSession.globalVersion || 1,
      visibleVersion: initialBatchImageEditSession.visibleVersion || 1,
      checkpointPromptsHistory:
        initialBatchImageEditSession.checkpointPromptsHistory || [],
      promptsHistory: initialBatchImageEditSession.promptsHistory || [],
    };
  }

  return {
    itemsMap: buildInitialItemsMap(initialItems),
    globalVersion: 1,
    visibleVersion: 1,
    checkpointPromptsHistory: [],
    promptsHistory: [],
  };
};

const getBlockBaseUrl = (itemState) =>
  itemState?.blockBaseUrl || itemState?.versions?.[1]?.url || null;

const getLatestTurnPrompt = (turns = []) => {
  const lastTurn = turns[turns.length - 1];
  return typeof lastTurn === "object" ? lastTurn?.prompt || "" : "";
};

export const useBatchImageEdit = ({
  initialItems = [],
  initialBatchImageEditSession = null,
  isOpen,
}) => {
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );

  const restoredState = buildRestoredBatchEditState({
    initialItems,
    initialBatchImageEditSession,
  });

  const [itemsById, setItemsById] = useState(() => restoredState.itemsMap);
  const [visibleVersion, setVisibleVersion] = useState(
    () => restoredState.visibleVersion
  );
  const [globalVersion, setGlobalVersion] = useState(
    () => restoredState.globalVersion
  );
  const [prompt, setPrompt] = useState(() =>
    getLatestTurnPrompt(restoredState.promptsHistory)
  );
  const [checkpointPromptsHistory, setCheckpointPromptsHistory] = useState(
    () => restoredState.checkpointPromptsHistory
  );
  const [isGenerateInProgress, setIsGenerateInProgress] = useState(false);
  const [retryingSelectionIds, setRetryingSelectionIds] = useState([]);

  const accumulatedAttachmentFileNamesRef = useRef({});
  const lastPromptsOrderedRef = useRef(restoredState.promptsHistory || []);
  const lastSessionHydrateKeyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      lastSessionHydrateKeyRef.current = null;
      return;
    }

    const sessionHydrateKey = initialBatchImageEditSession?.hydrateKey;
    if (
      sessionHydrateKey &&
      lastSessionHydrateKeyRef.current === sessionHydrateKey
    ) {
      return;
    }
    if (sessionHydrateKey) {
      lastSessionHydrateKeyRef.current = sessionHydrateKey;
    }

    const nextState = buildRestoredBatchEditState({
      initialItems,
      initialBatchImageEditSession,
    });

    setItemsById(nextState.itemsMap);
    setVisibleVersion(nextState.visibleVersion);
    setGlobalVersion(nextState.globalVersion);
    setPrompt(getLatestTurnPrompt(nextState.promptsHistory));
    setCheckpointPromptsHistory(nextState.checkpointPromptsHistory);
    lastPromptsOrderedRef.current = nextState.promptsHistory;
    accumulatedAttachmentFileNamesRef.current = {};
    setIsGenerateInProgress(false);
    setRetryingSelectionIds([]);
  }, [isOpen, initialItems, initialBatchImageEditSession]);

  const isSessionJobInProgress = Boolean(
    initialBatchImageEditSession?.isJobInProgress
  );
  const isGenerateLocked = isGenerateInProgress || isSessionJobInProgress;
  const isRetryInProgress = retryingSelectionIds.length > 0;
  const isJobInProgress = isGenerateLocked || isRetryInProgress;

  const versionOptions = useMemo(
    () => Array.from({ length: globalVersion }, (_, index) => index + 1),
    [globalVersion]
  );

  const items = useMemo(() => Object.values(itemsById), [itemsById]);

  const markItemsPending = useCallback((targetVersion, selectionIds) => {
    setItemsById((prev) => {
      const next = { ...prev };

      selectionIds.forEach((selectionId) => {
        if (!next[selectionId]) return;

        next[selectionId] = {
          ...next[selectionId],
          versions: {
            ...next[selectionId].versions,
            [targetVersion]: { status: BATCH_ITEM_STATUS.PENDING },
          },
        };
      });

      return next;
    });
  }, []);

  const applyBatchResults = useCallback((targetVersion, results = []) => {
    setItemsById((prev) => {
      const next = { ...prev };

      results.forEach((result) => {
        const selectionId = result?.selectionId;
        if (!selectionId || !next[selectionId]) return;

        const versions = { ...next[selectionId].versions };

        if (result.ok && result.url) {
          versions[targetVersion] = {
            url: result.url,
            status: BATCH_ITEM_STATUS.SUCCESS,
          };
        } else {
          versions[targetVersion] = {
            status: BATCH_ITEM_STATUS.ERROR,
            error: result.error || result.message || "",
          };
        }

        next[selectionId] = {
          ...next[selectionId],
          versions,
        };
      });

      return next;
    });
  }, []);

  const applyCheckpointBlockBases = useCallback((results = []) => {
    setItemsById((prev) => {
      const next = { ...prev };

      results.forEach((result) => {
        const selectionId = result?.selectionId;
        if (!selectionId || !result?.ok || !result?.url || !next[selectionId]) {
          return;
        }

        next[selectionId] = {
          ...next[selectionId],
          blockBaseUrl: result.url,
        };
      });

      return next;
    });
  }, []);

  const markItemsErrored = useCallback((targetVersion, selectionIds, error) => {
    setItemsById((prev) => {
      const next = { ...prev };

      selectionIds.forEach((selectionId) => {
        if (!next[selectionId]) return;

        next[selectionId] = {
          ...next[selectionId],
          versions: {
            ...next[selectionId].versions,
            [targetVersion]: {
              status: BATCH_ITEM_STATUS.ERROR,
              error: error || "",
            },
          },
        };
      });

      return next;
    });
  }, []);

  const runBatchEdit = useCallback(
    async ({ targetVersion, batchItems, prompts, trackAsGenerate = true }) => {
      const selectionIds = batchItems.map((item) => item.selectionId);
      const promptsOrdered = normalizeBatchPrompts(prompts);

      markItemsPending(targetVersion, selectionIds);
      if (trackAsGenerate) {
        setIsGenerateInProgress(true);
      }

      try {
        const response = await dispatch(
          startEditImagesBatch({
            items: batchItems,
            prompts: promptsOrdered,
            withAlert: trackAsGenerate,
          })
        );

        const batchResults = getBatchResultImages(response);

        if (response?.ok && batchResults.length > 0) {
          applyBatchResults(targetVersion, batchResults);
          return { ok: true, results: batchResults };
        }

        const errorMessage =
          typeof response?.message === "string" ? response.message : "";
        markItemsErrored(targetVersion, selectionIds, errorMessage);
        return { ok: false, results: batchResults };
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useBatchImageEdit",
            capture: "exception",
          })
        );
        const errorMessage =
          typeof error?.message === "string" ? error.message : "";
        markItemsErrored(targetVersion, selectionIds, errorMessage);
        return { ok: false, results: [] };
      } finally {
        if (trackAsGenerate) {
          setIsGenerateInProgress(false);
        }
      }
    },
    [applyBatchResults, dispatch, markItemsErrored, markItemsPending]
  );

  const validatePrompt = useCallback(
    (editPrompt) => {
      try {
        validateModalImageEditTextFields({
          prompt: editPrompt,
          isPlanActive,
          isTrial,
        });
        return true;
      } catch (error) {
        console.error("Batch image edit validation failed:", error);
        return false;
      }
    },
    [isPlanActive, isTrial]
  );

  const applyCheckpointAfterSuccess = useCallback(
    (promptsOrdered, batchResults) => {
      const enrichedTurns = enrichLastTurnWithResultUrls({
        turns: promptsOrdered,
        images: batchResults,
      });
      const completedTurns = getCompletedBatchTurns({
        turns: enrichedTurns,
        isSuccess: true,
        responseImages: batchResults,
      });
      const {
        checkpointPromptsHistory: nextCheckpointHistory,
        shouldResetCheckpoint,
      } = getBatchCheckpointState({ completedTurns });

      if (shouldResetCheckpoint) {
        applyCheckpointBlockBases(batchResults);
      }

      setCheckpointPromptsHistory(nextCheckpointHistory);
      lastPromptsOrderedRef.current = enrichedTurns;

      return enrichedTurns;
    },
    [applyCheckpointBlockBases]
  );

  const generate = useCallback(
    async (promptOverride, attachmentUrls = [], attachmentFileNames = {}) => {
      const trimmedPrompt = (promptOverride ?? prompt).trim();
      if (!trimmedPrompt || isJobInProgress || items.length === 0) {
        return { succeeded: false };
      }

      if (!validatePrompt(trimmedPrompt)) return { succeeded: false };

      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          showUsageConfirmationAlert: true,
          multiplier: items.length,
        })
      );
      if (isBlocked) return { cancelled: true };

      const targetVersion = globalVersion + 1;
      const resolvedAttachmentUrls = Array.isArray(attachmentUrls)
        ? attachmentUrls
        : [];
      const promptsOrdered = buildBatchPromptsForJob({
        history: checkpointPromptsHistory,
        prompt: trimmedPrompt,
        attachmentUrls: resolvedAttachmentUrls,
      });

      const latestResultUrls =
        checkpointPromptsHistory.length > 0
          ? checkpointPromptsHistory[checkpointPromptsHistory.length - 1]
              ?.resultUrls || {}
          : {};

      const batchItems = buildBatchItemsForJob({
        items: items.map((itemState) => ({
          selectionId: itemState.selectionId,
          url: getBlockBaseUrl(itemState),
        })),
        resultUrls: latestResultUrls,
      });

      if (batchItems.length === 0) return { succeeded: false };

      setPrompt(trimmedPrompt);
      setVisibleVersion(targetVersion);
      accumulatedAttachmentFileNamesRef.current = mergeAttachmentFileNames(
        accumulatedAttachmentFileNamesRef.current,
        attachmentFileNames
      );

      const { ok, results } = await runBatchEdit({
        targetVersion,
        batchItems,
        prompts: promptsOrdered,
        trackAsGenerate: true,
      });

      if (ok) {
        applyCheckpointAfterSuccess(promptsOrdered, results);
        setGlobalVersion(targetVersion);
        return { succeeded: true, targetVersion };
      }

      return { succeeded: false, targetVersion };
    },
    [
      ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      applyCheckpointAfterSuccess,
      checkpointPromptsHistory,
      dispatch,
      globalVersion,
      isJobInProgress,
      items,
      prompt,
      runBatchEdit,
      validatePrompt,
    ]
  );

  const retryItem = useCallback(
    async (selectionId) => {
      const promptsOrdered = lastPromptsOrderedRef.current;
      const itemState = itemsById[selectionId];
      const targetVersion = visibleVersion;

      if (
        !selectionId ||
        !itemState ||
        targetVersion <= 1 ||
        isGenerateLocked ||
        retryingSelectionIds.includes(selectionId) ||
        promptsOrdered.length === 0
      ) {
        return;
      }

      const versionEntry = itemState.versions[targetVersion];
      if (versionEntry?.status !== BATCH_ITEM_STATUS.ERROR) return;

      const sourceUrl = getBlockBaseUrl(itemState);
      if (!sourceUrl) return;

      const latestPrompt = getLatestTurnPrompt(promptsOrdered);
      if (!validatePrompt(latestPrompt)) return;

      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          showUsageConfirmationAlert: true,
          multiplier: 1,
        })
      );
      if (isBlocked) return;

      setRetryingSelectionIds((prev) =>
        prev.includes(selectionId) ? prev : [...prev, selectionId]
      );

      try {
        const { ok, results } = await runBatchEdit({
          targetVersion,
          batchItems: [{ selectionId, url: sourceUrl }],
          prompts: promptsOrdered,
          trackAsGenerate: false,
        });

        if (ok && results.length > 0) {
          applyCheckpointAfterSuccess(promptsOrdered, results);
        }
      } finally {
        setRetryingSelectionIds((prev) =>
          prev.filter((id) => id !== selectionId)
        );
      }
    },
    [
      ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      applyCheckpointAfterSuccess,
      dispatch,
      isGenerateLocked,
      itemsById,
      retryingSelectionIds,
      runBatchEdit,
      validatePrompt,
      visibleVersion,
    ]
  );

  const getVisibleEntry = useCallback(
    (itemState) => {
      if (!itemState) return null;

      const directEntry = itemState.versions[visibleVersion];
      if (directEntry) return directEntry;

      for (let version = visibleVersion; version >= 1; version -= 1) {
        if (itemState.versions[version]) {
          return itemState.versions[version];
        }
      }

      return itemState.versions[1] || null;
    },
    [visibleVersion]
  );

  const hasApplyableSuccesses = useMemo(() => {
    if (visibleVersion <= 1) return false;

    return items.some(
      (itemState) =>
        itemState.versions[visibleVersion]?.status === BATCH_ITEM_STATUS.SUCCESS
    );
  }, [items, visibleVersion]);

  const getSuccessfulItemsForApply = useCallback(() => {
    return items
      .filter(
        (itemState) =>
          itemState.versions[visibleVersion]?.status ===
          BATCH_ITEM_STATUS.SUCCESS
      )
      .map((itemState) => ({
        selectionId: itemState.selectionId,
        url: itemState.versions[visibleVersion].url,
        sourceItem: itemState.sourceItem,
        index: itemState.index,
      }));
  }, [items, visibleVersion]);

  return {
    items,
    visibleVersion,
    setVisibleVersion,
    globalVersion,
    versionOptions,
    prompt,
    setPrompt,
    isJobInProgress,
    isGenerateInProgress: isGenerateLocked,
    isRetryInProgress,
    retryingSelectionIds,
    generate,
    retryItem,
    hasApplyableSuccesses,
    getSuccessfulItemsForApply,
    getVisibleEntry,
  };
};
